// Import React hooks
import { useState, useEffect, useCallback, useRef } from "react";



// Exported hook in PascalCase per your convention
export const usePersistentFilters = (

  // A unique identifier for the specific table using this hook
  tableId,

  // The list of filter keys that this table owns
  filterKeys = [],

  // The default filters to apply when nothing exists in URL or localStorage
  defaultFilters = {},

  // Optional configuration object
  options

) => {

  
  // Ref to track whether initialisation has completed
  const initializedRef = useRef(false);


  // Resolve localStorage namespace
  const storageNamespace = (options && options.storageNamespace && options.storageNamespace.trim()) || "filters";


  // Resolve URL param separator
  const urlParamSeparator = ((options && options.urlParamSeparator) ?? ":").trim() || ":";


  // Build the localStorage key using the namespace and the tableId
  const storageKey = `${storageNamespace}:${tableId}`;



  // Helper to create a namespaced URL param key like "<tableId>:<key>"
  const toParamKey = useCallback(

    // Return the namespaced key
    (key) => `${tableId}${urlParamSeparator}${key}`,

    // Dependencies
    [tableId, urlParamSeparator]

  );



  // This checks whether a filters object has any selected values in any group
  const hasAnyFilterValues = useCallback((filtersObject) => {

    // Return true if at least one filter group contains one or more values
    return Object.values(filtersObject || {}).some((values) => {

      // Only count non-empty arrays as real selected values
      return Array.isArray(values) && values.length > 0;

    });

  }, []);



  // State for the currently selected filters
  const [selectedFilters, setSelectedFilters] = useState(() => {

    // If window is unavailable, return empty object
    if (typeof window === "undefined") {

      // Return empty object
      return {};

    }

    // Read current URL params
    const params = new URLSearchParams(window.location.search);

    // Prepare an object to hold filters parsed from URL
    const initialFromUrl = {};

    // If specific keys were provided, read only those
    if (Array.isArray(filterKeys) && filterKeys.length > 0) {

      // Loop over each declared key
      filterKeys.forEach((key) => {

        // Build namespaced param name
        const paramName = toParamKey(key);

        // Read raw value from URL
        const raw = params.get(paramName);

        // Only add the key when a real value exists
        if (raw) {

          // Split the raw value into an array
          initialFromUrl[key] = raw.split(",");

        }

      });

    } else {

      // If no keys were provided, scan URL for this table's namespaced params
      const prefix = `${tableId}${urlParamSeparator}`;

      // Iterate all URL params
      params.forEach((_value, name) => {

        // Only process params that belong to this table
        if (name.startsWith(prefix)) {

          // Extract the actual key after the prefix
          const key = name.slice(prefix.length);

          // Read the raw value for the param
          const raw = params.get(name);

          // Only add the key when a real value exists
          if (raw) {

            // Split the raw value into an array
            initialFromUrl[key] = raw.split(",");

          }

        }

      });

    }

    // Check whether URL contributed any real selected values
    const urlHasValues = Object.values(initialFromUrl).some((values) => {

      // Only count non-empty arrays
      return Array.isArray(values) && values.length > 0;

    });

    // If URL has values, prefer those
    if (urlHasValues) {

      // Return filters from URL
      return initialFromUrl;

    }

    // Otherwise try localStorage
    try {

      // Read the table-scoped entry
      const stored = localStorage.getItem(storageKey);

      // Parse stored filters if present
      const parsedStored = stored ? JSON.parse(stored) : null;

      // If localStorage has real filter values, use them
      if (
        parsedStored &&
        typeof parsedStored === "object" &&
        Object.values(parsedStored).some((values) => Array.isArray(values) && values.length > 0)
      ) {

        // Return stored filters
        return parsedStored;

      }

    } catch (e) {

      // Ignore parse and storage errors
    }

    // If defaults have real values, use them
    if (
      defaultFilters &&
      typeof defaultFilters === "object" &&
      Object.values(defaultFilters).some((values) => Array.isArray(values) && values.length > 0)
    ) {

      // Return default filters
      return defaultFilters;

    }

    // Final fallback
    return {};

  });



  // This completes initialisation once defaults are available if needed
  useEffect(() => {

    // Stop if initialisation has already completed
    if (initializedRef.current) {

      // Exit early
      return;

    }

    // Check whether selected filters already contain real values
    const hasSelectedFilters = hasAnyFilterValues(selectedFilters);

    // If selected filters already exist, mark initialised and stop
    if (hasSelectedFilters) {

      // Mark as initialised
      initializedRef.current = true;

      // Exit early
      return;

    }

    // Stop until default filters are available
    if (!defaultFilters || Object.keys(defaultFilters).length === 0) {

      // Exit early
      return;

    }

    // Check whether default filters contain real values
    const hasDefaultValues = hasAnyFilterValues(defaultFilters);

    // If defaults contain real values, apply them
    if (hasDefaultValues) {

      // Apply the provided default filters
      setSelectedFilters(defaultFilters);

    }

    // Mark initialisation as complete
    initializedRef.current = true;

  }, [defaultFilters, selectedFilters, hasAnyFilterValues]);



  // Effect to persist to localStorage and sync URL
  useEffect(() => {

    // Do not persist until initialization has completed
    if (!initializedRef.current) {

      // Exit early
      return;

    }

    // Guard for non-browser environments
    if (typeof window === "undefined") {

      // Exit early
      return;

    }

    // Persist to localStorage under this table's key
    try {

      // Serialize current filters
      const serialized = JSON.stringify(selectedFilters);

      // Store safely
      localStorage.setItem(storageKey, serialized);

    } catch (e) {

      // Ignore storage errors
    }

    // Start with current search params so we preserve unrelated query params
    const params = new URLSearchParams(window.location.search);

    // Compute the prefix for this table
    const prefix = `${tableId}${urlParamSeparator}`;

    // Collect all param names that belong to this table
    const namesToDelete = [];

    // Iterate all params to find table-scoped ones
    params.forEach((_value, name) => {

      // If the param belongs to this table, mark it for deletion
      if (name.startsWith(prefix)) {

        // Add the name to the deletion list
        namesToDelete.push(name);

      }

    });

    // Remove old table-scoped params so we can write fresh ones
    namesToDelete.forEach((name) => {

      // Delete the old param
      params.delete(name);

    });

    // Write the current selection back under the table's namespace
    Object.entries(selectedFilters || {}).forEach(([key, values]) => {

      // Only write non-empty arrays
      if (Array.isArray(values) && values.length > 0) {

        // Build namespaced param name
        const paramName = toParamKey(key);

        // Join array values by comma
        params.set(paramName, values.join(","));

      }

    });

    // Build the query string
    const queryString = params.toString();

    // Build the new URL while preserving unrelated query params
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    // Replace the current history entry to avoid pushing a new one
    window.history.replaceState(null, "", newUrl);

  }, [selectedFilters, storageKey, tableId, urlParamSeparator, toParamKey]);



  // This toggles a single filter value on or off
  const toggleFilterValue = useCallback((key, value) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Get the current included values for this group
      const currentValues = previousFilters[key] || [];

      // Check whether the value is already included
      const isAlreadySelected = currentValues.includes(value);

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: isAlreadySelected
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value],
      };

    });

  }, []);



  // This toggles all values in a group on or off depending on whether all are selected
  const toggleSelectAllFilters = useCallback((key, allValues = []) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Get the current included values for this group
      const currentValues = previousFilters[key] || [];

      // Check whether all values are already selected
      const isEverythingSelected = currentValues.length === allValues.length;

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: isEverythingSelected ? [] : [...allValues],
      };

    });

  }, []);



  // This checks whether all values are selected for a filter group
  const isAllSelected = useCallback((key, allValues = []) => {

    // Read selected values or empty array
    const selectedValues = selectedFilters[key] || [];

    // Return true when lengths match
    return selectedValues.length === allValues.length;

  }, [selectedFilters]);



  // This clears a single filter group
  const clearFilterGroup = useCallback((key) => {

    // Update by merging with an empty array for this key
    setSelectedFilters((previousFilters) => ({

      // Keep existing groups
      ...previousFilters,

      // Clear this group
      [key]: [],

    }));

  }, []);



  // This clears all filters for this table
  const clearAllFilters = useCallback(() => {

    // Reset the table's filter state
    setSelectedFilters({});

  }, []);



  // This includes every value in a filter group
  const setFilterGroupAllOn = useCallback((key, values = []) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: [...values],
      };

    });

  }, []);



  // This excludes every value in a filter group
  const setFilterGroupAllOff = useCallback((key) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: [],
      };

    });

  }, []);



  // This resets a filter group back to its default values
  const resetFilterGroup = useCallback((key) => {

    // Update the filters state
    setSelectedFilters((previousFilters) => {

      // Return the updated filter state
      return {
        ...previousFilters,
        [key]: [...(defaultFilters[key] || [])],
      };

    });

  }, [defaultFilters]);





  



  // Public API returned by the hook
  return {

    // Current selection object
    selectedFilters,

    // Function to update the selection
    setSelectedFilters,

    // Toggle a single value inside a filter key
    toggleFilterValue,

    // Toggle all values for a key
    toggleSelectAllFilters,

    // Check if all values are selected for a key
    isAllSelected,

    // Clear one filter key
    clearFilterGroup,

    // Clear all filter keys for this table
    clearAllFilters,

    // Helpers for toggle-switch based filter groups
    setFilterGroupAllOn,
    setFilterGroupAllOff,
    resetFilterGroup,

  };

};